import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Search, RefreshCw, FileText, Loader2 } from "lucide-react";
import { birpApi } from "../api/birpApi";
import { individualsApi } from "@/lib/planApi";
import BirpReportListItem from "../components/BirpReportListItem";
import BirpReportEditor from "../components/BirpReportEditor";
import BirpGenerateDialog from "../components/BirpGenerateDialog";

const STATUS_TABS = [
    { value: "all", label: "All" },
    { value: "draft", label: "Draft" },
    { value: "in_review", label: "In review" },
    { value: "approved", label: "Approved" },
];

const BirpReportsPage = () => {
    const [individuals, setIndividuals] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [individualFilter, setIndividualFilter] = useState(null);
    const [query, setQuery] = useState("");

    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [generating, setGenerating] = useState(false);

    // Individuals for the generate dialog + filter
    useEffect(() => {
        individualsApi
            .list({ active: true })
            .then((rows) => setIndividuals(rows || []))
            .catch(() => setIndividuals([]));
    }, []);

    const fetchReports = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: 1, page_size: 100 };
            if (statusFilter !== "all") params.status = statusFilter;
            if (individualFilter) params.individual_id = individualFilter;
            const data = await birpApi.listReports(params);
            setReports(data.items || []);
        } catch (err) {
            toast.error(err?.message || "Could not load reports");
            setReports([]);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, individualFilter]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    // Load detail when selection changes
    useEffect(() => {
        if (!selectedId) {
            setDetail(null);
            return;
        }
        setDetailLoading(true);
        birpApi
            .getReport(selectedId)
            .then((r) => setDetail(r))
            .catch(() => setDetail(null))
            .finally(() => setDetailLoading(false));
    }, [selectedId]);

    // Filter list by search query on individual name
    const filteredReports = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return reports;
        return reports.filter((r) =>
            (r.individual_name || "").toLowerCase().includes(q),
        );
    }, [reports, query]);

    const handleGenerate = async (payload) => {
        setGenerating(true);
        try {
            const result = await birpApi.generate(payload);
            toast.success("Report generated with Claude");
            setDialogOpen(false);
            // Refresh list + select the new report
            await fetchReports();
            setSelectedId(result.id);
            setDetail(result);
        } catch (err) {
            const raw = err?.data?.detail;
            const msg =
                (raw && typeof raw === "object" && raw.message) ||
                (typeof raw === "string" && raw) ||
                err?.message ||
                "Generate failed";
            toast.error(msg);
        } finally {
            setGenerating(false);
        }
    };

    const handleReportChanged = (updated) => {
        setDetail(updated);
        setReports((prev) =>
            prev.map((r) =>
                r.id === updated.id
                    ? {
                          ...r,
                          status: updated.status,
                          approved_at: updated.approved_at,
                          modified_at: updated.modified_at,
                      }
                    : r,
            ),
        );
    };

    const handleReportDeleted = (id) => {
        setReports((prev) => prev.filter((r) => r.id !== id));
        setSelectedId(null);
        setDetail(null);
    };

    return (
        <div
            data-testid="birp-page"
            className="flex h-[calc(100dvh-8rem)] flex-col gap-4"
        >
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-heading font-black tracking-tight text-slate-900">
                        Individual BIRP Report
                    </h1>
                    <p className="text-sm text-slate-500">
                        AI-generated progress notes · Behavior · Intervention ·
                        Response · Plan
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={fetchReports}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                        data-testid="birp-refresh"
                    >
                        <RefreshCw
                            className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                        />
                        Refresh
                    </button>
                    <button
                        type="button"
                        onClick={() => setDialogOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-blue-700 px-3 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-brand-blue-800"
                        data-testid="birp-open-generate"
                    >
                        <Sparkles className="h-4 w-4" />
                        Generate report
                    </button>
                </div>
            </div>

            {/* Split view */}
            <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[360px_1fr]">
                {/* LEFT: filters + list */}
                <div className="flex min-h-0 flex-col rounded-2xl border border-slate-200 bg-white/85 backdrop-blur-md">
                    <div className="border-b border-slate-100 p-3">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                            <input
                                type="search"
                                placeholder="Search individual…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                data-testid="birp-search"
                                className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30"
                            />
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                            {STATUS_TABS.map((t) => (
                                <button
                                    key={t.value}
                                    type="button"
                                    onClick={() => setStatusFilter(t.value)}
                                    data-testid={`birp-filter-status-${t.value}`}
                                    data-active={statusFilter === t.value || undefined}
                                    className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
                                        statusFilter === t.value
                                            ? "bg-brand-blue-700 text-white"
                                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                    }`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        {individuals.length > 1 && (
                            <select
                                value={individualFilter || ""}
                                onChange={(e) =>
                                    setIndividualFilter(e.target.value || null)
                                }
                                data-testid="birp-filter-individual"
                                className="mt-2 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs"
                            >
                                <option value="">
                                    {`All individuals (${individuals.length})`}
                                </option>
                                {individuals.map((i) => (
                                    <option key={i.id} value={i.id}>
                                        {`${i.first_name} ${i.last_name}`}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                    <div
                        className="flex-1 space-y-2 overflow-y-auto p-3"
                        data-testid="birp-report-list"
                    >
                        {loading && (
                            <div className="p-4 text-center text-xs text-slate-500">
                                <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                                Loading…
                            </div>
                        )}
                        {!loading && filteredReports.length === 0 && (
                            <div className="p-8 text-center" data-testid="birp-empty">
                                <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                                <p className="text-sm font-semibold text-slate-500">
                                    No reports yet
                                </p>
                                <p className="mt-1 text-xs text-slate-400">
                                    Click <b>Generate report</b> to create one.
                                </p>
                            </div>
                        )}
                        {!loading &&
                            filteredReports.map((r) => (
                                <BirpReportListItem
                                    key={r.id}
                                    report={r}
                                    active={selectedId === r.id}
                                    onSelect={() => setSelectedId(r.id)}
                                />
                            ))}
                    </div>
                </div>

                {/* RIGHT: editor */}
                <div className="min-h-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    {!selectedId && (
                        <div
                            className="flex h-full items-center justify-center p-6 text-center"
                            data-testid="birp-editor-empty"
                        >
                            <div>
                                <FileText className="mx-auto mb-2 h-10 w-10 text-slate-300" />
                                <p className="text-sm font-semibold text-slate-500">
                                    Select a report on the left
                                </p>
                                <p className="mt-1 text-xs text-slate-400">
                                    Or click Generate report to create one now.
                                </p>
                            </div>
                        </div>
                    )}
                    {selectedId && detailLoading && (
                        <div
                            className="flex h-full items-center justify-center"
                            data-testid="birp-editor-loading"
                        >
                            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                        </div>
                    )}
                    {selectedId && !detailLoading && detail && (
                        <BirpReportEditor
                            report={detail}
                            onChange={handleReportChanged}
                            onDeleted={handleReportDeleted}
                        />
                    )}
                </div>
            </div>

            <BirpGenerateDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                individuals={individuals}
                defaultIndividualId={individualFilter}
                onGenerate={handleGenerate}
                generating={generating}
            />
        </div>
    );
};

export default BirpReportsPage;
